/**
 * A PERMISSÃO DE AÇÃO NÃO ABRE TELA NENHUMA — e era isso que ninguém dizia.
 *
 * Relato do dono (27/10/2026): *"eu quero liberar o módulo de combustíveis e,
 * dentro dele, a aba de lançamento… fui lá, busquei abastecimento, liberei.
 * Quando entrei no acesso do cara, o cara não tem o acesso, porque parece que
 * ele não tem acesso ao módulo de combustíveis"*.
 *
 * Ele estava certo, e o sistema estava certo — cada um do seu lado. Neste
 * sistema a tela pede a licença de LER o módulo (`LEITURA_DA_TELA`), e a ação
 * dentro dela pede a licença própria. Conceder `abastecimento.registrar` sem
 * `combustivel.ler` produz exatamente o que ele viu: a pessoa PODE lançar e
 * não vê onde. Nem o menu, nem a tela, nem uma frase explicando.
 *
 * ## O que este módulo faz, e o que ele recusa fazer
 *
 * Ele responde "de que leitura esta permissão depende?" — pelo MÓDULO dela,
 * que é o dado que a própria permissão carrega. A regra tem três casos, e o
 * terceiro é o que impede o conserto de virar outro problema:
 *
 *   UMA leitura no módulo    a tela leva-a junto, dizendo que levou. É o caso
 *                            do 08 (`combustivel.ler`) e da maioria.
 *   VÁRIAS                   NÃO leva nenhuma sozinha: o módulo 02 tem cinco
 *                            (catálogo, equipamento, prontuário, alocação,
 *                            situação), e escolher por quem concede seria dar
 *                            acesso que ninguém pediu. A tela NOMEIA as
 *                            candidatas e deixa a escolha.
 *   NENHUMA                  não há o que dizer. O módulo 13 é assim: as
 *                            telas dele leem a licença de outro.
 *
 * Ler é de baixo risco, mas não é de risco zero — `prontuario.ler` abre o
 * histórico inteiro de uma máquina. Por isso a inclusão automática acontece só
 * quando não há escolha a fazer, e sempre à vista, marcada na lista.
 */

export interface PermissaoComModulo {
  readonly id: string;
  readonly codigo: string;
  readonly modulo: string;
}

/** A licença de LER tem esta forma no catálogo inteiro: `assunto.ler`. */
const EH_LEITURA = /\.ler$/;

/** As leituras de um módulo, na ordem do catálogo. */
export function leiturasDoModulo(
  todas: readonly PermissaoComModulo[],
  modulo: string,
): readonly PermissaoComModulo[] {
  return todas.filter((p) => p.modulo === modulo && EH_LEITURA.test(p.codigo));
}

export interface LeituraQueFalta {
  /** A leitura a incluir. Nula quando o módulo tem várias, ou nenhuma. */
  readonly incluir: PermissaoComModulo | null;
  /** As candidatas, quando há mais de uma e a escolha é de quem concede. */
  readonly escolher: readonly PermissaoComModulo[];
  /** Os códigos de ação que dependem disso, para a frase nomear o caso. */
  readonly acoes: readonly string[];
}

/**
 * O que falta para as permissões escolhidas abrirem alguma tela.
 *
 * `jaTem` é o que o papel já tem concedido: a leitura que ele já possui não é
 * cobrada de novo, senão a frase apareceria para sempre e seria ignorada.
 */
export function leiturasQueFaltam(
  escolhidas: readonly PermissaoComModulo[],
  jaTem: ReadonlySet<string>,
  todas: readonly PermissaoComModulo[],
): readonly LeituraQueFalta[] {
  const porModulo = new Map<string, string[]>();
  for (const p of escolhidas) {
    /* A própria leitura não depende de leitura. Sem isto, escolher
       `combustivel.ler` sozinha pediria `combustivel.ler`. */
    if (EH_LEITURA.test(p.codigo)) continue;
    porModulo.set(p.modulo, [...(porModulo.get(p.modulo) ?? []), p.codigo]);
  }

  const escolhidasPorCodigo = new Set(escolhidas.map((p) => p.codigo));
  const faltas: LeituraQueFalta[] = [];
  for (const [modulo, acoes] of [...porModulo.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const leituras = leiturasDoModulo(todas, modulo);
    /* Já coberta: o papel tem a leitura, ou ela está na mesma escolha. */
    if (leituras.some((l) => jaTem.has(l.codigo) || escolhidasPorCodigo.has(l.codigo))) continue;
    if (leituras.length === 0) continue;
    faltas.push({
      incluir: leituras.length === 1 ? (leituras[0] ?? null) : null,
      escolher: leituras.length === 1 ? [] : leituras,
      acoes,
    });
  }
  return faltas;
}

/**
 * As ações JÁ CONCEDIDAS que não abrem nada — o retrato do papel do dono
 * depois de ele conceder só `abastecimento.registrar`.
 *
 * É o aviso que faltava: sem ele, o papel parece montado, a pessoa entra e
 * não acha a tela, e quem concedeu procura o erro no lugar errado.
 */
export function acoesSemLeitura(
  concedidas: readonly PermissaoComModulo[],
  todas: readonly PermissaoComModulo[],
): readonly { readonly modulo: string; readonly acoes: readonly string[]; readonly leituras: readonly PermissaoComModulo[] }[] {
  const tem = new Set(concedidas.map((p) => p.codigo));
  const porModulo = new Map<string, string[]>();
  for (const p of concedidas) {
    if (EH_LEITURA.test(p.codigo)) continue;
    porModulo.set(p.modulo, [...(porModulo.get(p.modulo) ?? []), p.codigo]);
  }

  const semLeitura: { modulo: string; acoes: readonly string[]; leituras: readonly PermissaoComModulo[] }[] = [];
  for (const [modulo, acoes] of [...porModulo.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const leituras = leiturasDoModulo(todas, modulo);
    if (leituras.length === 0 || leituras.some((l) => tem.has(l.codigo))) continue;
    semLeitura.push({ modulo, acoes, leituras });
  }
  return semLeitura;
}
